'use client';

import { useEffect, useState, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { usePathname, useSearchParams } from 'next/navigation';
import { useEvent, useUnit } from 'effector-react';
import classNames from 'classnames';
import { DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SidebarSkeleton } from '@nx-frontend/shared/ui/skeletons';
import { CollapseAnimationWrapper } from '@nx-frontend/shared/components';
import { $customizerPreviewFullscreen } from '@nx-frontend/shared/store/customizer-preview-store';
import { initCustomizerMenuEvent } from '@nx-frontend/shared/store/feature-menu-tree';
import { $isNoteFullScreen } from '@nx-frontend/shared/store/note-fullscreen';
import { $opCanCreatePortal } from '@nx-frontend/shared/store/org-role';
import { $portalSettings } from '@nx-frontend/shared/store/portal';
import { $isCustomizerMode, $path } from '@nx-frontend/shared/store/portal-app';
import {
  $isSidebarCollapsed,
  $isSidebarResizing,
  $sidebar,
  setIsSidebarCollapsedAndSaveFx,
} from '@nx-frontend/shared/store/sidebar';
import { useInitialLoad, useSidebarDnd } from './hooks';
import { SidebarKitchenMenu } from './kitchen/sidebar-kitchen-menu';
import { SidebarPortalSection } from './portal/sidebar-portal-section';
import { SidebarActionsBlock } from './sidebar-actions-bar';
import { SidebarResizeLine } from './sidebar-resize-line';
import { SIDEBAR_SECTION_TYPE, SidebarSection } from './sidebar-section';
import { SidebarWorkspaceSelect } from './sidebar-workspace-select';

export const Sidebar = ({ initialWidth }: { initialWidth?: number }) => {
  const t = useTranslations('sidebar');
  const [
    isNoteFullScreen,
    sidebar,
    path,
    isCustomizer,
    isSidebarCollapsed,
    customizerPreviewFullscreen,
    isSidebarResizing,
    portalSettings,
    opCanCreatePortal,
  ] = useUnit([
    $isNoteFullScreen,
    $sidebar,
    $path,
    $isCustomizerMode,
    $isSidebarCollapsed,
    $customizerPreviewFullscreen,
    $isSidebarResizing,
    $portalSettings,
    $opCanCreatePortal,
  ]);
  const [prevPath, setPrevPath] = useState('');
  const [showPortalSection, setShowPortalSection] = useState(false);
  const [menuInit, setIsSidebarCollapsed] = useEvent([initCustomizerMenuEvent, setIsSidebarCollapsedAndSaveFx]);
  const pathname = usePathname();
  const { dragStart, dragEnd } = useSidebarDnd();
  const searchParams = useSearchParams();
  const [prevSearch, setPrevSearch] = useState(searchParams.toString());
  const ref = useRef<HTMLDivElement>(null);
  const searchParamsString = searchParams.toString();
  useInitialLoad();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(KeyboardSensor)
  );

  useEffect(() => {
    if (portalSettings.id || opCanCreatePortal) {
      setShowPortalSection(true);
    } else {
      setShowPortalSection(false);
    }
  }, [opCanCreatePortal, portalSettings.id]);

  // This is needed for the proper rendering of the sidebar's width on the backend.
  const sidebarWidth = typeof window === 'undefined' && initialWidth ? initialWidth : sidebar.width;

  useEffect(() => {
    setPrevPath(pathname);
  }, [pathname]);

  useEffect(() => {
    if (searchParamsString !== prevSearch) {
      setPrevSearch(searchParamsString);
    }
  }, [prevSearch, searchParamsString]);

  useEffect(() => {
    if ((prevPath !== path || searchParamsString !== prevSearch) && isCustomizer) {
      menuInit();
    }
  }, [isCustomizer, menuInit, path, pathname, prevPath, prevSearch, searchParamsString]);

  const sidebarRef = useRef<HTMLDivElement>(null);

  const toggleCollapsed = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  useEffect(() => {
    let resizeObserver: ResizeObserver | null = null;
    if (sidebarRef.current) {
      resizeObserver = new ResizeObserver(() => {
        window.dispatchEvent(new CustomEvent('sidebar:resize', { detail: { width: sidebarRef.current?.clientWidth } }));
      });
      resizeObserver.observe(sidebarRef.current);
    }
    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, []);

  if (customizerPreviewFullscreen) {
    return null;
  }

  return (
    <div
      className={classNames('h-full', {
        hidden: isNoteFullScreen,
      })}
      ref={sidebarRef}
    >
      <CollapseAnimationWrapper
        collapsed={isSidebarCollapsed}
        width={sidebarWidth}
        showControl={!customizerPreviewFullscreen}
        controlPosition="right"
        transitionOff={isSidebarResizing}
        controlClick={toggleCollapsed}
      >
        <>
          <div className="app-sidebar h-screen overflow-hidden grow-0 shrink-0 drop-shadow-md bg-white relative z-[4]">
            <div
              className={classNames('overflow-hidden h-screen')}
              ref={ref}
              style={{ width: `${sidebarWidth}px` }}
              data-cy="sidebar-wrapper"
            >
              <div
                className="px-4 pt-5 pb-2 grow-0 shrink-0 text-neutral-800 h-full w-full overflow-y-auto overflow-x-auto"
                id="sidebar-scroll-container"
              >
                {!sidebar.isLoaded && <SidebarSkeleton />}
                {sidebar.isLoaded && (
                  <>
                    <SidebarWorkspaceSelect />
                    <div className="mt-4">
                      <SidebarActionsBlock />
                    </div>
                    <DndContext
                      sensors={sensors}
                      onDragEnd={dragEnd}
                      modifiers={[restrictToVerticalAxis]}
                      onDragStart={dragStart}
                    >
                      <SidebarSection title={t('sidebar-internal_space')} type={SIDEBAR_SECTION_TYPE.INTERNAL_SPACE}>
                        <SidebarKitchenMenu />
                      </SidebarSection>
                      {showPortalSection && (
                        <SidebarSection title={t('sidebar-client_portal')} type={SIDEBAR_SECTION_TYPE.CLIENT_PORTAL}>
                          <SidebarPortalSection />
                        </SidebarSection>
                      )}
                    </DndContext>
                  </>
                )}
              </div>
            </div>
          </div>
          {!sidebar.isCollapsed && <SidebarResizeLine sidebarRef={ref} offset={0} />}
        </>
      </CollapseAnimationWrapper>
    </div>
  );
};
